'use client';

import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// Types
// ============================================================================

export type ToastType = 'info' | 'success' | 'warning' | 'error';
export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ToastData {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// ============================================================================
// Context
// ============================================================================

interface ToastContextValue {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToastContext() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider');
  }
  return context;
}

// ============================================================================
// ToastProvider
// ============================================================================

export interface ToastProviderProps {
  children: React.ReactNode;
  position?: ToastPosition;
  maxToasts?: number;
}

export function ToastProvider({
  children,
  position = 'top-right',
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  const addToast = React.useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 11);
      setToasts((prev) => {
        const newToasts = [...prev, { ...toast, id }];
        return newToasts.slice(-maxToasts);
      });
      return id;
    },
    [maxToasts]
  );

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearToasts = React.useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue: ToastContextValue = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer position={position} />
    </ToastContext.Provider>
  );
}

// ============================================================================
// ToastContainer
// ============================================================================

export interface ToastContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  position?: ToastPosition;
}

export function ToastContainer({
  position = 'top-right',
  className,
  ...props
}: ToastContainerProps) {
  const context = React.useContext(ToastContext);
  const toasts = context?.toasts || [];

  const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className={clsx(
        'toast-container fixed z-50 flex flex-col gap-2',
        positionClasses[position],
        className
      )}
      {...props}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} />
      ))}
    </div>
  );
}

// ============================================================================
// Toast
// ============================================================================

export interface ToastProps extends ToastData {
  onDismiss?: () => void;
}

export function Toast({
  id,
  type,
  title,
  message,
  duration = 5000,
  dismissible = true,
  action,
  onDismiss,
}: ToastProps) {
  const context = React.useContext(ToastContext);
  const [isExiting, setIsExiting] = React.useState(false);

  const handleDismiss = React.useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      context?.removeToast(id);
      onDismiss?.();
    }, 200);
  }, [context, id, onDismiss]);

  React.useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(handleDismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, handleDismiss]);

  const typeStyles = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'text-blue-500',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: 'text-green-500',
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: 'text-yellow-500',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: 'text-red-500',
    },
  };

  const icons = {
    info: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const styles = typeStyles[type];

  return (
    <div
      role="alert"
      className={clsx(
        'toast flex items-start gap-3 p-4 rounded-lg border shadow-lg',
        'min-w-[300px] max-w-md',
        styles.bg,
        styles.border,
        'transition-all duration-200',
        isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'
      )}
    >
      <span className={clsx('flex-shrink-0', styles.icon)}>{icons[type]}</span>

      <div className="flex-1 min-w-0">
        {title && <p className="font-medium text-gray-900 dark:text-gray-100">{title}</p>}
        <p className={clsx('text-sm text-gray-600 dark:text-gray-300', title && 'mt-1')}>
          {message}
        </p>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            {action.label}
          </button>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Alert
// ============================================================================

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: ToastType;
  title?: string;
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export function Alert({
  type = 'info',
  title,
  icon,
  dismissible = false,
  onDismiss,
  actions,
  children,
  className,
  ...props
}: AlertProps) {
  const [isDismissed, setIsDismissed] = React.useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) return null;

  const typeStyles = {
    info: {
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      border: 'border-blue-200 dark:border-blue-800',
      icon: 'text-blue-500',
      title: 'text-blue-800 dark:text-blue-200',
    },
    success: {
      bg: 'bg-green-50 dark:bg-green-900/20',
      border: 'border-green-200 dark:border-green-800',
      icon: 'text-green-500',
      title: 'text-green-800 dark:text-green-200',
    },
    warning: {
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      border: 'border-yellow-200 dark:border-yellow-800',
      icon: 'text-yellow-600',
      title: 'text-yellow-800 dark:text-yellow-200',
    },
    error: {
      bg: 'bg-red-50 dark:bg-red-900/20',
      border: 'border-red-200 dark:border-red-800',
      icon: 'text-red-500',
      title: 'text-red-800 dark:text-red-200',
    },
  };

  const defaultIcons = {
    info: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const styles = typeStyles[type];

  return (
    <div
      role="alert"
      className={clsx(
        'alert flex gap-3 p-4 rounded-lg border',
        styles.bg,
        styles.border,
        className
      )}
      {...props}
    >
      <span className={clsx('flex-shrink-0', styles.icon)}>
        {icon || defaultIcons[type]}
      </span>

      <div className="flex-1 min-w-0">
        {title && <p className={clsx('font-medium', styles.title)}>{title}</p>}
        <div className={clsx('text-sm text-gray-600 dark:text-gray-300', title && 'mt-1')}>
          {children}
        </div>
        {actions && <div className="mt-3 flex gap-2">{actions}</div>}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss alert"
          className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Banner
// ============================================================================

export interface BannerProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: ToastType;
  position?: 'top' | 'bottom';
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  action?: {
    label: string;
    onClick: () => void;
  };
  children: React.ReactNode;
}

export function Banner({
  type = 'info',
  position = 'top',
  dismissible = true,
  onDismiss,
  icon,
  action,
  children,
  className,
  ...props
}: BannerProps) {
  const [isDismissed, setIsDismissed] = React.useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  if (isDismissed) return null;

  const typeStyles = {
    info: 'bg-blue-600 text-white',
    success: 'bg-green-600 text-white',
    warning: 'bg-yellow-500 text-black dark:text-white',
    error: 'bg-red-600 text-white',
  };

  const positionClasses = {
    top: 'top-0',
    bottom: 'bottom-0',
  };

  return (
    <div
      role="banner"
      className={clsx(
        'banner fixed left-0 right-0 z-50',
        'px-4 py-3',
        typeStyles[type],
        positionClasses[position],
        className
      )}
      {...props}
    >
      <div className="container mx-auto flex items-center justify-center gap-4">
        {icon && <span className="flex-shrink-0">{icon}</span>}

        <p className="text-sm font-medium">{children}</p>

        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className={clsx(
              'flex-shrink-0 px-3 py-1 rounded text-sm font-medium',
              'bg-white/20 hover:bg-white/30 transition-colors dark:bg-gray-800'
            )}
          >
            {action.label}
          </button>
        )}

        {dismissible && (
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="flex-shrink-0 p-1 rounded hover:bg-white/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Snackbar
// ============================================================================

export interface SnackbarProps extends React.HTMLAttributes<HTMLDivElement> {
  open?: boolean;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  onClose?: () => void;
}

export function Snackbar({
  open = false,
  message,
  action,
  duration = 5000,
  onClose,
  className,
  ...props
}: SnackbarProps) {
  React.useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => onClose?.(), duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  if (!open) return null;

  return (
    <div
      role="alert"
      className={clsx(
        'snackbar fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-4 px-4 py-3',
        'bg-gray-800 dark:bg-gray-700 text-white',
        'rounded-lg shadow-lg',
        'animate-in slide-in-from-bottom-4 fade-in duration-200',
        className
      )}
      {...props}
    >
      <p className="text-sm">{message}</p>

      {action && (
        <button
          type="button"
          onClick={() => {
            action.onClick();
            onClose?.();
          }}
          className="flex-shrink-0 text-sm font-medium text-blue-400 hover:text-blue-300"
        >
          {action.label}
        </button>
      )}

      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="flex-shrink-0 p-1 rounded hover:bg-gray-700"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ============================================================================
// Notification
// ============================================================================

export interface NotificationProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  avatar?: React.ReactNode;
  time?: string;
  unread?: boolean;
  actions?: React.ReactNode;
  onDismiss?: () => void;
  onClick?: () => void;
}

export function Notification({
  title,
  description,
  avatar,
  time,
  unread = false,
  actions,
  onDismiss,
  onClick,
  className,
  ...props
}: NotificationProps) {
  return (
    <div
      className={clsx(
        'notification flex gap-3 p-4 rounded-lg',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        unread && 'border-l-4 border-l-blue-500',
        onClick && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {avatar && <div className="flex-shrink-0">{avatar}</div>}

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={clsx('text-sm font-medium', unread && 'text-blue-600 dark:text-blue-400')}>
            {title}
          </p>
          {time && (
            <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
              {time}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
            {description}
          </p>
        )}
        {actions && <div className="mt-2 flex gap-2">{actions}</div>}
      </div>

      {onDismiss && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss notification"
          className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <svg className="w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// NotificationCenter
// ============================================================================

export interface NotificationItem {
  id: string;
  title: string;
  description?: string;
  avatar?: React.ReactNode;
  time?: string;
  unread?: boolean;
  onClick?: () => void;
}

export interface NotificationCenterProps extends React.HTMLAttributes<HTMLDivElement> {
  notifications: NotificationItem[];
  onDismiss?: (id: string) => void;
  onClearAll?: () => void;
  onMarkAllRead?: () => void;
  emptyMessage?: string;
  title?: string;
}

export function NotificationCenter({
  notifications,
  onDismiss,
  onClearAll,
  onMarkAllRead,
  emptyMessage = 'No notifications',
  title = 'Notifications',
  className,
  ...props
}: NotificationCenterProps) {
  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <div
      className={clsx(
        'notification-center w-80 max-h-96 overflow-hidden',
        'bg-white dark:bg-gray-800',
        'border border-gray-200 dark:border-gray-700',
        'rounded-lg shadow-lg',
        className
      )}
      {...props}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{title}</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onMarkAllRead && unreadCount > 0 && (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              Mark all read
            </button>
          )}
          {onClearAll && notifications.length > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="overflow-auto max-h-80">
        {notifications.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {emptyMessage}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {notifications.map((notification) => (
              <Notification
                key={notification.id}
                title={notification.title}
                description={notification.description}
                avatar={notification.avatar}
                time={notification.time}
                unread={notification.unread}
                onClick={notification.onClick}
                onDismiss={onDismiss ? () => onDismiss(notification.id) : undefined}
                className="rounded-none border-0"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// InlineAlert
// ============================================================================

export interface InlineAlertProps extends React.HTMLAttributes<HTMLDivElement> {
  type?: ToastType;
  children: React.ReactNode;
}

export function InlineAlert({
  type = 'info',
  children,
  className,
  ...props
}: InlineAlertProps) {
  const typeStyles = {
    info: 'text-blue-600 dark:text-blue-400',
    success: 'text-green-600 dark:text-green-400',
    warning: 'text-yellow-600 dark:text-yellow-400',
    error: 'text-red-600 dark:text-red-400',
  };

  const icons = {
    info: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    success: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    error: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  return (
    <div
      className={clsx('inline-alert inline-flex items-center gap-1.5 text-sm', typeStyles[type], className)}
      {...props}
    >
      {icons[type]}
      <span>{children}</span>
    </div>
  );
}

// ============================================================================
// useToast Hook
// ============================================================================

export interface UseToastOptions {
  position?: ToastPosition;
  defaultDuration?: number;
}

export interface UseToastReturn {
  toasts: ToastData[];
  toast: (options: Omit<ToastData, 'id'>) => string;
  info: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  success: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  warning: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  error: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export function useToast(options: UseToastOptions = {}): UseToastReturn {
  const { defaultDuration = 5000 } = options;
  const [toasts, setToasts] = React.useState<ToastData[]>([]);

  const addToast = React.useCallback(
    (toast: Omit<ToastData, 'id'>) => {
      const id = Math.random().toString(36).substring(2, 11);
      const newToast = { ...toast, id, duration: toast.duration ?? defaultDuration };
      setToasts((prev) => [...prev, newToast]);

      if (newToast.duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, newToast.duration);
      }

      return id;
    },
    [defaultDuration]
  );

  const dismiss = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = React.useCallback(() => {
    setToasts([]);
  }, []);

  const info = React.useCallback(
    (message: string, opts?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) =>
      addToast({ type: 'info', message, ...opts }),
    [addToast]
  );

  const success = React.useCallback(
    (message: string, opts?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) =>
      addToast({ type: 'success', message, ...opts }),
    [addToast]
  );

  const warning = React.useCallback(
    (message: string, opts?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) =>
      addToast({ type: 'warning', message, ...opts }),
    [addToast]
  );

  const error = React.useCallback(
    (message: string, opts?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) =>
      addToast({ type: 'error', message, ...opts }),
    [addToast]
  );

  return {
    toasts,
    toast: addToast,
    info,
    success,
    warning,
    error,
    dismiss,
    dismissAll,
  };
}
